import get from 'lodash/get';
import has from 'lodash/has';
import React, { useCallback, useMemo } from 'react';
import { StyledComponent } from 'styled-components';

import { RenderItemProps } from 'canoe/components/List';
import { FormikFieldSetter } from 'craft/types';

import { MultiSelect } from './MultiSelect';
import { SingleSelect } from './SingleSelect';

export interface DefaultItemProps {
  label: string;
  value: string;
  deprecated?: boolean;
  disabled?: boolean;
}

export interface SelectProps<Item = DefaultItemProps, Values = any> {
  name: string;
  readOnly?: boolean;
  options: Item[];
  placeholder?: string;
  emptyPlaceholder?: string;
  onChange?: (item: Item) => void;
  onSearch?: (value: string) => void;
  searchValue?: string;
  onClearSearch?: () => void;
  renderItem?: (itemProps: RenderItemProps<Item>) => React.ReactNode;
  loading?: boolean;
  selectFirstByDefault?: boolean;
  allowCustomValues?: boolean;
  isMultiselect?: boolean;
  InputWrapper?: StyledComponent<'div', any, { isMultiselect?: boolean | undefined }>;
  'data-cy'?: string;
  values: Values;
  setFieldValue: FormikFieldSetter;
  validateOnSelectWithErrors?: boolean;
  onValueNotFound?: () => { showEmptyValue: boolean; warningText: string };
}

export const Select = <Item extends DefaultItemProps, Values>({
  isMultiselect,
  onChange,
  allowCustomValues,
  onValueNotFound,
  ...props
}: SelectProps<Item, Values>) => {
  const { values } = props;

  const { name, options, searchValue, loading } = props;
  const currentValue = useMemo(() => get(values, name) || '', [values, name]);

  const valueIsEmpty = useMemo(() => {
    if (!currentValue) {
      return true;
    }
    if (Array.isArray(currentValue) && currentValue.length === 0) {
      return true;
    }
    return Array.isArray(currentValue) && currentValue.length === 1 && currentValue[0] === null;
  }, [currentValue]);
  const valueIsArray = useMemo(() => !valueIsEmpty && Array.isArray(currentValue), [currentValue]);

  const foundItem = options.find(
    (item) =>
      !item.disabled &&
      (valueIsArray ? currentValue.includes(item.value) : item.value === currentValue),
  );
  const validOptions = options.filter(
    (option) =>
      (valueIsArray ? currentValue.includes(option.value) : option.value === currentValue) ||
      !option.deprecated,
  );

  const noAvailableOptions = useMemo(
    () =>
      !loading &&
      !has(values, name) &&
      (!validOptions || validOptions.length === 0) &&
      (!searchValue || searchValue === ''),
    [loading, validOptions, searchValue, values, name],
  );

  const getItems = useCallback(
    (newOptions?: Item[]) => {
      const found =
        searchValue &&
        (newOptions || validOptions).find(
          ({ label }: Item) => label.toLowerCase() === searchValue?.toLowerCase(),
        );
      const prependSearchToOptions = allowCustomValues && !found && searchValue?.trim();

      const formattedOptions = [
        ...(prependSearchToOptions && searchValue
          ? [
              {
                label: searchValue.trim(),
                value: searchValue.trim(),
              },
            ]
          : []),
        ...validOptions,
        ...(newOptions || []),
      ] as Item[];

      if (!searchValue) {
        return formattedOptions;
      }

      return formattedOptions.filter((option: Item) => {
        if (
          typeof option.value === 'string' &&
          option.value.toLowerCase().includes(searchValue.toLowerCase())
        ) {
          return true;
        }

        if (
          typeof option.label === 'string' &&
          option.label.toLowerCase().includes(searchValue.toLowerCase())
        ) {
          return true;
        }

        return false;
      });
    },
    [validOptions, searchValue, allowCustomValues],
  );

  return isMultiselect ? (
    <MultiSelect<Item, Values>
      {...props}
      currentValue={valueIsArray ? currentValue : currentValue.toString()}
      valueIsEmpty={valueIsEmpty}
      valueIsArray={valueIsArray}
      noAvailableOptions={noAvailableOptions && !allowCustomValues}
      getItems={getItems}
    />
  ) : (
    <SingleSelect<Item, Values>
      onChange={onChange}
      allowCustomValues={allowCustomValues}
      {...props}
      currentValue={valueIsArray ? currentValue : currentValue.toString()}
      valueIsArray={valueIsArray}
      noAvailableOptions={noAvailableOptions && !allowCustomValues}
      getItems={getItems}
      errorMessage={
        foundItem?.deprecated ? 'This option is deprecated. Please use another one.' : undefined
      }
      handleEmptyValue={!foundItem && onValueNotFound ? onValueNotFound() : undefined}
    />
  );
};
